import { TextCell } from "@similarweb/pro-logic-components";

import HooksModalV5Key from "app/constants/HooksModalV5Key";
import { NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import {
    createLocationCodesCellValue,
    createPrimitiveListCellValue,
    isUpgradeCellValuePresent,
    ListCell,
    LocationGroupCell,
    PriceCell,
    type PrimitiveListCellValue,
    renderAbbrNumberWithThresholdCellFactory,
    renderDateCellFactory,
    renderRatingCellFactory,
} from "app/pages/sales-intelligence/common-components/table-cells";
import TechnologiesCell from "app/pages/sales-intelligence/common-components/table-cells/components/TechnologiesCell/TechnologiesCell";
import { createColumnFactoryList } from "app/pages/sales-intelligence/common-components/search-results-table/utils/columns";
import { MIN_RELIABLE_METRIC_VALUE } from "app/pages/sales-intelligence/constants/threshold";
import LeadCellContainer from "app/pages/sales-intelligence/modules/leads-search/containers/search-results-table/LeadCell/LeadCellContainer";
import { SiUpgradeCellContainer } from "app/pages/sales-intelligence/modules/leads-search/containers/SiUpgradeCellContainer/SiUpgradeCellContainer";

import AppNameCell from "../../components/cells/AppNameCell";
import BusinessModelsCell from "../../components/cells/BusinessModelsCell";
import type { AppSearchRowDto, SdkRowDto } from "../../types/rows";
import type { AppsColumnListConfig, AppsConfigColumnsRecord } from "../../types/table";

import { buildLeadCellRecord, createDynamicColumnFactory, createRegularColumnFactory } from "./utils";

const abbrNumber = abbrNumberFilter();

const renderAbbrNumberWithThresholdCell = renderAbbrNumberWithThresholdCellFactory({ min: MIN_RELIABLE_METRIC_VALUE });

const formatSdksArray = (value: readonly PrimitiveListCellValue[]) =>
    translate("si.find_apps.table.column.content.sdk", { count: value.length });

export const createAppsSearchColumnListConfig = (): AppsColumnListConfig =>
    createColumnFactoryList([
        [
            "app",
            createRegularColumnFactory({
                dataIndex: "app",
                width: 284,
                render: (value: AppsConfigColumnsRecord["app"], row) => (
                    <AppNameCell
                        value={value}
                        row={row}
                    />
                ),
            }),
        ],
        [
            "developer_name",
            createRegularColumnFactory({
                dataIndex: "developer_name",
                ellipsis: true,
            }),
        ],
        [
            "country",
            createRegularColumnFactory({
                dataIndex: "country",
                render: (value: AppsConfigColumnsRecord["country"], _record, index) => {
                    const locationCodes = createLocationCodesCellValue(value);

                    return (
                        <SiUpgradeCellContainer isUpgradeVisible={locationCodes[0] === -1}>
                            <LocationGroupCell
                                locationCodes={locationCodes}
                                recordIndex={index}
                            />
                        </SiUpgradeCellContainer>
                    );
                },
            }),
        ],
        [
            "website",
            createRegularColumnFactory({
                dataIndex: "website",
                ellipsis: true,
            }),
        ],
        [
            "monthly_downloads",
            createRegularColumnFactory({
                dataIndex: "monthly_downloads",
                tooltipTitle: translate(`si.find_apps.table.column.tooltip-title.all.monthly_downloads`),
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "monthly_active_users",
            createRegularColumnFactory({
                dataIndex: "monthly_active_users",
                align: "end",
                render: (value: AppsConfigColumnsRecord["monthly_active_users"]) => (
                    <TextCell>{value === 0 ? NOT_AVAILABLE : abbrNumber(value)}</TextCell>
                ),
            }),
        ],
        [
            "last_updated",
            createRegularColumnFactory({
                dataIndex: "last_updated",
                render: (value: AppsConfigColumnsRecord["last_updated"]) => renderDateCellFactory()(value),
            }),
        ],
        [
            "release_date",
            createRegularColumnFactory({
                dataIndex: "release_date",
                render: (value: AppsConfigColumnsRecord["release_date"]) =>
                    value == null ? <TextCell>{NOT_AVAILABLE}</TextCell> : renderDateCellFactory()(value),
            }),
        ],
        [
            "price",
            createRegularColumnFactory({
                dataIndex: "price",
                align: "end",
                width: 95,
                render: (value: AppsConfigColumnsRecord["price"]) => <PriceCell value={value} />,
            }),
        ],
        [
            "rating",
            createRegularColumnFactory({
                dataIndex: "rating",
                align: "end",
                width: 95,
                render: renderRatingCellFactory(),
            }),
        ],
        [
            "number_of_ratings",
            createRegularColumnFactory({
                dataIndex: "number_of_ratings",
                align: "end",
                render: renderAbbrNumberWithThresholdCell,
            }),
        ],
        [
            "sdk",
            createDynamicColumnFactory({
                width: 150,
                render: (value: AppsConfigColumnsRecord[SdkRowDto]) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatSdksArray}
                    />
                ),
            }),
        ],
        [
            "sdkCategory",
            createDynamicColumnFactory({
                width: 150,
                render: (value: AppsConfigColumnsRecord[SdkRowDto]) => (
                    <TechnologiesCell
                        value={value}
                        formatArrayValue={formatSdksArray}
                    />
                ),
            }),
        ],
        [
            "permissions",
            createRegularColumnFactory({
                dataIndex: "permissions",
                render: (value: AppsConfigColumnsRecord["permissions"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="app-permissions"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "business_models",
            createRegularColumnFactory({
                dataIndex: "business_models",
                render: (value: AppsConfigColumnsRecord["business_models"]) => <BusinessModelsCell value={value} />,
            }),
        ],
        [
            "ads_sellers",
            createRegularColumnFactory({
                dataIndex: "ads_sellers",
                render: (value: AppsConfigColumnsRecord["ads_sellers"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="ads-sellers"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "iab_categories_paths",
            createRegularColumnFactory({
                dataIndex: "iab_categories_paths",
                width: 250,
                render: (value: AppsConfigColumnsRecord["iab_categories_paths"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="iab_categories_paths"
                        tooltipPlacement="left"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "main_domain",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "main_domain",
                width: 260,
                title: translate("si.find_apps.table.column.title.company"),
                tooltipTitle: translate("si.find_apps.table.column.tooltip-title.company"),
                render: (value: AppsConfigColumnsRecord["main_domain"]) => {
                    if (!value) {
                        return null;
                    }

                    const isUpgrade = isUpgradeCellValuePresent(value);
                    const record = isUpgrade ? null : buildLeadCellRecord(value);

                    return (
                        <SiUpgradeCellContainer
                            isUpgradeVisible={isUpgrade}
                            featureKey={HooksModalV5Key.SalesSolutionDefault}
                        >
                            {record ? (
                                <LeadCellContainer
                                    leadsType="companies"
                                    record={record}
                                >
                                    {record.company}
                                </LeadCellContainer>
                            ) : null}
                        </SiUpgradeCellContainer>
                    );
                },
            }),
        ],
        [
            "package_name",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "package_name",
                ellipsis: true,
            }),
        ],
        [
            "app_category",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "app_category",
                ellipsis: true,
            }),
        ],
        [
            "total_downloads",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "total_downloads",
                align: "end",
            }),
        ],
        [
            "track_id",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "track_id",
                ellipsis: true,
            }),
        ],
        [
            "categories",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "categories",
                render: (value: AppsConfigColumnsRecord["categories"]) => (
                    <ListCell
                        value={value}
                        dataAutomation="app-categories"
                    >
                        {createPrimitiveListCellValue({ value })}
                    </ListCell>
                ),
            }),
        ],
        [
            "bundle_id",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "bundle_id",
                ellipsis: true,
            }),
        ],
        [
            "primary_category",
            createRegularColumnFactory<AppSearchRowDto>({
                dataIndex: "primary_category",
                ellipsis: true,
            }),
        ],
        [
            "email",
            createRegularColumnFactory({
                dataIndex: "email",
                ellipsis: true,
            }),
        ],
    ]);
